import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { ScheduleHeader } from "@/components/schedule/ScheduleHeader";
import { WeeklySchedule } from "@/components/schedule/WeeklySchedule";
import { CalendarSwitcher } from "@/components/schedule/CalendarSwitcher";
import { ScheduledClass } from "@/types/types";
import { ScheduleCalendar } from "./ScheduleCalendar.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Settings2, LayoutGrid, List, CalendarDays, Wifi, WifiOff, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import CoachDailySchedule from "@/pages/CoachDailySchedule.tsx";
import { useUserStore } from "@/store/userStore";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { listCalendars } from "@/services/edgeFunctionWrappers/calendarsWrapper";
import { useRealtimeSchedule } from "@/hooks/useRealtimeSchedule";
import { startOfWeek, endOfWeek } from "date-fns";
import { scheduleClassesService } from "@/services/scheduleClassesService";
import {
  getViewSwitchTargetDate,
  type ScheduleViewMode,
} from "./scheduleViewNavigation";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";

// « agenda » : même liste colorée par jour que l'espace adhérent.
type ViewMode = ScheduleViewMode;

interface ScheduleContentProps {
  weekDates: Date[];
  selectedDate: Date;
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  onPrevious: () => void;
  onNext: () => void;
  onDateSelect: (date: Date) => void;
  onAddClass: (date: Date, time: string) => void;
  onEditClass: (classData: ScheduledClass, date: Date) => void;
  classes: ScheduledClass[];
  isLoading: boolean;
  refreshClasses?: () => Promise<void>;
  selectedCalendarId?: string | null;
  setSelectedCalendarId?: (id: string | null) => void;
}

export function ScheduleContent({
  weekDates,
  selectedDate,
  currentMonth,
  onMonthChange,
  onPrevious,
  onNext,
  onDateSelect,
  onAddClass,
  onEditClass,
  classes,
  isLoading,
  refreshClasses,
  selectedCalendarId = null,
  setSelectedCalendarId,
}: ScheduleContentProps) {
  const { t } = useTranslation(["schedule", "common", "bookingui"]);
  const navigate = useNavigate();
  const { gymId, isLoadingGymSwitch, role } = useUserStore();
  const user = useUserStore((state) => state.user);
  const { athleteThemeClass } = useThemeCustomization();
  const queryClient = useQueryClient();
  const prevGymIdRef = useRef(gymId);
  const isStaffSchedule = role === "admin" || role === "coach";

  const handleRealtimeRefresh = React.useCallback(() => {
    if (user?.id && gymId) {
      scheduleClassesService.invalidateCache(user.id, gymId);
      queryClient.invalidateQueries({ queryKey: ["schedule-classes", user.id, gymId] });
      queryClient.refetchQueries({ queryKey: ["schedule-classes", user.id, gymId], type: "active" });
    } else {
      queryClient.invalidateQueries({ queryKey: ["schedule-classes"] });
      queryClient.refetchQueries({ queryKey: ["schedule-classes"], type: "active" });
    }
    refreshClasses?.();
  }, [gymId, queryClient, refreshClasses, user?.id]);

  const { connectionStatus } = useRealtimeSchedule({
    enabled: isStaffSchedule && !!gymId && !!user?.id,
    gymId: gymId || "",
    startDate: startOfWeek(selectedDate, { weekStartsOn: 1 }),
    endDate: endOfWeek(selectedDate, { weekStartsOn: 1 }),
    onScheduleChange: handleRealtimeRefresh,
    onBookingChange: handleRealtimeRefresh,
    showNotifications: false,
  });

  const { data: calendars = [] } = useQuery({
    queryKey: ["calendars", gymId],
    queryFn: () => listCalendars(gymId ?? "", false),
    enabled: !!gymId,
    staleTime: 60000,
  });

  // ✅ Invalider le cache React Query quand le gymId change
  useEffect(() => {
    if (prevGymIdRef.current && prevGymIdRef.current !== gymId && !isLoadingGymSwitch) {
      console.log('[ScheduleContent] Gym changed, invalidating React Query cache');
      // Remove all schedule-related queries to force fresh fetch
      queryClient.removeQueries({ queryKey: ['schedule-classes'] });
      queryClient.removeQueries({ queryKey: ['daily-classes'] });
      
      // Trigger refresh if available
      if (refreshClasses) {
        refreshClasses();
      }
    }
    prevGymIdRef.current = gymId;
  }, [gymId, isLoadingGymSwitch, queryClient, refreshClasses]);

  // état interne de la vue — PAS contrôlé par le parent (fiable)
  const [mode, setMode] = useState<ViewMode>("daily");

  // Bascule de vue façon espace adhérent : pilule arrondie avec icônes.
  const viewOptions: Array<{ value: ViewMode; label: string; icon: typeof LayoutGrid }> = [
    { value: "weekly", label: t("schedule:weekView", "Semaine"), icon: LayoutGrid },
    { value: "agenda", label: t("schedule:agendaView", "Agenda"), icon: List },
    { value: "daily", label: t("schedule:dayView", "Jour"), icon: CalendarDays },
  ];

  const handleViewChange = (nextMode: ViewMode) => {
    setMode(nextMode);

    const targetDate = getViewSwitchTargetDate(nextMode);
    if (!targetDate) return;

    onDateSelect(targetDate);
    onMonthChange(targetDate);
  };

  return (
    <div className={cn(athleteThemeClass === "athlete-shell--vb20" && "vb20-admin-schedule", role === "coach" && "coach-schedule")}> 
      <ScheduleCalendar
        selectedDate={selectedDate}
        currentMonth={currentMonth}
        onDateSelect={(d) => {
          onDateSelect(d);
          // 👉 décommente pour basculer automatiquement en vue jour au clic calendrier
           setMode("daily");
        }}
        onMonthChange={onMonthChange}
        compact={role === "coach" && athleteThemeClass === "athlete-shell--vb20"}
      />

      <Card className="coach-schedule-panel w-full">
        <CardHeader className="coach-schedule-toolbar space-y-2 md:space-y-3 px-3 md:px-6">
          {/* Barre d'outils façon espace adhérent : sélecteur de planning coloré
              à gauche, bascule de vue (pilule) à droite. */}
          <div className="flex flex-wrap items-center justify-between gap-2 md:gap-3">
            {setSelectedCalendarId ? (
              <div className="flex items-center gap-2">
                {/* Peu de plannings (cas courant) : sélecteur segmenté coloré,
                    façon espace adhérent. Beaucoup de plannings : on garde le
                    menu déroulant, plus compact. */}
                {calendars.length > 0 && calendars.length <= 4 ? (
                  <CalendarSwitcher
                    calendars={calendars}
                    selectedCalendarId={selectedCalendarId ?? null}
                    onSelect={setSelectedCalendarId}
                    allLabel={t("common:all_calendars", "Tous")}
                    ariaLabel={t("common:all_calendars", "Choisir un planning")}
                  />
                ) : (
                  <Select
                    value={selectedCalendarId ?? "__all__"}
                    onValueChange={(v) => setSelectedCalendarId(v === "__all__" ? null : v)}
                  >
                    <SelectTrigger className="w-[140px] md:w-[200px] text-xs md:text-sm h-8 md:h-10">
                      <SelectValue placeholder={t("common:all_calendars", "Tous les calendriers")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__" className="text-xs md:text-sm">{t("common:all_calendars", "Tous les calendriers")}</SelectItem>
                      {calendars.map((cal) => (
                        <SelectItem key={cal.id} value={cal.id} className="text-xs md:text-sm">
                          {cal.color && (
                            <span
                              className="mr-2 inline-block h-2.5 w-2.5 md:h-3 md:w-3 rounded-full"
                              style={{ backgroundColor: cal.color }}
                            />
                          )}
                          {cal.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {role === "admin" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-8 w-8 md:h-10 md:w-10"
                    title={t("common:manage_calendars", "Gérer les calendriers")}
                    onClick={() => navigate("/admin/calendars")}
                  >
                    <Settings2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  </Button>
                )}
              </div>
            ) : (
              <span />
            )}

            {/* `relative z-10 pointer-events-auto` : hérité de l'ancienne
                TabsList, qui portait ce contournement contre un overlay qui
                avalait les clics. Conservé par prudence. */}
            <div
              className="coach-view-switcher relative z-10 inline-flex items-center gap-0.5 rounded-full border border-border bg-muted/60 p-1 pointer-events-auto"
              role="group"
              aria-label={t("schedule:viewMode", "Affichage")}
            >
              {viewOptions.map(({ value, label, icon: Icon }) => (
                <Button
                  key={value}
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-pressed={mode === value}
                  onClick={() => handleViewChange(value)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium transition-colors md:px-3 md:text-sm",
                    mode === value
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{label}</span>
                </Button>
              ))}
            </div>

            {isStaffSchedule && (
              <div
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium",
                  connectionStatus === "connected"
                    ? "border-primary/30 bg-primary/10 text-primary"
                    : connectionStatus === "connecting"
                      ? "border-border bg-muted text-muted-foreground"
                      : "border-destructive/30 bg-destructive/10 text-destructive"
                )}
                aria-live="polite"
              >
                {connectionStatus === "connected" ? (
                  <Wifi className="h-3 w-3" />
                ) : connectionStatus === "connecting" ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <WifiOff className="h-3 w-3" />
                )}
                <span className="hidden sm:inline">
                  {connectionStatus === "connected"
                    ? t("connection.connected", { ns: "bookingui" })
                    : connectionStatus === "connecting"
                      ? t("connection.connecting", { ns: "bookingui" })
                      : t("connection.disconnected", { ns: "bookingui" })}
                </span>
              </div>
            )}
          </div>

          <ScheduleHeader
            viewMode={mode === "daily" ? "daily" : "weekly"}
            weekDates={weekDates}
            selectedDate={selectedDate}
            onPrevious={onPrevious}
            onNext={onNext}
          />
        </CardHeader>

        <CardContent className="overflow-hidden px-3 md:px-6">
          {mode === "daily" ? (
            <CoachDailySchedule
              initialDate={selectedDate}
              onDateChange={onDateSelect}
              selectedCalendarId={selectedCalendarId}
            />
          ) : (
            <WeeklySchedule
              weekDates={weekDates}
              selectedDate={selectedDate}
              onDateSelect={onDateSelect}
              onAddClass={onAddClass}
              onEditClass={onEditClass}
              classes={classes}
              isLoading={isLoading}
              refreshClasses={refreshClasses}
              selectedCalendarId={selectedCalendarId}
              viewMode={mode === "agenda" ? "agenda" : "week"}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
